import { getFlag } from '@nx/launch-darkly-service';
import { neo4jVersionUtil } from '@nx/neo4j-version-utils';
import type { FleetManagementTypes, Instance, MetricsResult, TimeRange } from '@nx/state';
import { MODAL_TYPE, TIER, useActiveProject, useModal } from '@nx/state';
import { isNonEmptyString, isNotNullish } from '@nx/stdlib';
import { Grid } from '@nx/ui';
import { useMemo } from 'react';

import { NoInstancesOverlay, UpgradeOverlay } from '../shared/components';
import { fakeMetrics } from '../shared/utils';
import { AuraDatabaseCharts } from './charts/database-charts';
import type { ChartType } from './charts/fullstack-chart-props';
import { AuraInstanceCharts } from './charts/instance-charts';
import { LineCard, OptionalFeatures } from './charts/line-cards';
import { AuraSystemCharts } from './charts/system-charts';
import type { ChartContext, ChartExpandProps, MultiChartProps } from './charts/types';
import type { MetricsViewState } from './hooks';
import { chartKey, useMetricsMetadata, useMetricsQuery } from './hooks';
import { useViewCharts } from './hooks/use-view-charts';
import { getPills } from './shared/utils';

const ViewCharts: Record<ChartType, MultiChartProps[]> = {
  HOST: AuraSystemCharts,
  INSTANCE: AuraInstanceCharts,
  DB: AuraDatabaseCharts,
  INSTANCE_DASHBOARD: [],
  DEPLOYMENT_DASHBOARD: [],
};

const TIME_RANGE_PADDED = true;
const MIN_PADDING_MS = 5 * 60 * 1000;
const PADDING_PERCENTAGE = 0.1;

const getPadding = ({ startTime, endTime }: TimeRange) => {
  const diff = endTime.getTime() - startTime.getTime();
  return Math.max(MIN_PADDING_MS, diff * PADDING_PERCENTAGE);
};
export const padTimeRange = (timeRange: TimeRange): TimeRange => {
  const paddingMs = getPadding(timeRange);
  const startTime = new Date(timeRange.startTime.getTime() - paddingMs);
  const endTime = new Date(timeRange.endTime.getTime() + paddingMs);
  return {
    startTime,
    endTime,
  };
};

type MetricsViewProps = {
  instance?: Instance;
  dbmsDetails?: FleetManagementTypes.DbmsDetails;
  viewType: ChartType;
  viewState: MetricsViewState;
  chartContext: ChartContext;
  expandProps: ChartExpandProps;
  onZoom?: (timeRange: TimeRange) => void | undefined;
  onZoomReset?: () => void | undefined;
  useAZGrouping: boolean;
  showLeaderOnly?: boolean;
  database?: string;
};

const MetricsView = ({
  instance,
  dbmsDetails,
  viewType,
  chartContext,
  viewState,
  expandProps,
  onZoom,
  onZoomReset,
  useAZGrouping,
  showLeaderOnly,
  database,
}: MetricsViewProps) => {
  const isUpxConsistencyEnabled = getFlag('upxConsistency', false);
  const GridContainer = isUpxConsistencyEnabled ? Grid : 'div';
  const containerProps = isUpxConsistencyEnabled
    ? {}
    : { className: 'grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3' };

  const activeProject = useActiveProject();
  const { timeRangeZoomedIn, expandedChart } = viewState;
  const timeRange = timeRangeZoomedIn ? padTimeRange(chartContext.timeRange) : chartContext.timeRange;
  const viewChartProps = useViewCharts(instance, useAZGrouping)[viewType];
  const { metricsMetadata, isError: isMetricsMetadataErr } = useMetricsMetadata(
    isNotNullish(instance) || isNotNullish(dbmsDetails) ? activeProject.id : undefined,
  );
  const isDbmsEnterprise = instance?.tier === TIER.ENTERPRISE || instance?.tier === TIER.MTE;

  const shallowChartProps = useMemo(() => {
    return viewChartProps.map((value) => {
      // Only request the first metric of each multi-chart
      return Array.isArray(value) && value.length > 1 ? value[0]! : value;
    });
  }, [viewChartProps]);

  const selectedEntityDetails = useMemo(() => {
    return {
      tenantId: instance?.projectId ?? activeProject.id,
      dbmsId: instance?.id ?? dbmsDetails?.dbmsId ?? '',
      name: instance?.name ?? dbmsDetails?.name ?? '',
    };
  }, [instance, dbmsDetails, activeProject.id]);

  const servers = useMemo(() => {
    return Object.fromEntries((dbmsDetails?.servers ?? []).map(({ serverId, address }) => [serverId, address]));
  }, [dbmsDetails]);

  const metricsResult = useMetricsQuery({
    selectedEntityDetails,
    timeRange,
    chartProps: shallowChartProps,
    chartType: viewType,
    useAggregations: isDbmsEnterprise && !useAZGrouping,
    useAZGrouping: useAZGrouping,
    showLeaderOnly: showLeaderOnly,
    database,
    isFleetDbms: isNotNullish(dbmsDetails),
    servers,
  });

  const instanceFulfillsVersionRequirement = (chartProps: MultiChartProps) => {
    const minVersion = (Array.isArray(chartProps) ? chartProps[0] : chartProps)?.minimumRequiredDatabaseVersion;
    if (isNonEmptyString(minVersion) && isNotNullish(instance) && instance.desiredSettings.version) {
      return neo4jVersionUtil.gte(instance.desiredSettings.version, minVersion);
    }
    return true;
  };

  return (
    <GridContainer {...containerProps}>
      {viewChartProps.map(
        (chart) =>
          instanceFulfillsVersionRequirement(chart) && (
            <LineCard
              key={chartKey(chart)}
              multiChartProps={chart}
              context={chartContext}
              metricsResult={metricsResult}
              expandProps={{ ...expandProps, isExpanded: expandedChart === chart }}
              onZoom={onZoom}
              onZoomReset={onZoomReset}
              timeRangePadded={TIME_RANGE_PADDED}
              addMinMaxBands={true}
              pills={getPills(chart, useAZGrouping, isNotNullish(dbmsDetails), metricsResult.metrics, showLeaderOnly)}
              isCmiAvailable={
                instance?.tier === TIER.ENTERPRISE || instance?.tier === TIER.MTE || instance?.tier === TIER.AURA_DSE
              }
              optionalFeatures={OptionalFeatures.AuraWithLogs}
              metadata={!isMetricsMetadataErr ? metricsMetadata : undefined}
              timeRange={timeRange}
              entityDetails={selectedEntityDetails}
              useAggregations={isDbmsEnterprise && !useAZGrouping}
              useAZGrouping={useAZGrouping}
              showLeaderOnly={showLeaderOnly}
              database={database}
              isFleetDbms={isNotNullish(dbmsDetails)}
              servers={servers}
            />
          ),
      )}
    </GridContainer>
  );
};

const FakeMetricsView = ({
  instanceName,
  viewType,
  chartContext,
}: {
  instanceName: string;
  viewType: ChartType;
  chartContext: ChartContext;
}) => {
  const isUpxConsistencyEnabled = getFlag('upxConsistency', false);
  const GridContainer = isUpxConsistencyEnabled ? Grid : 'div';
  const containerProps = isUpxConsistencyEnabled
    ? {}
    : { className: 'grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3' };

  const chartProps = ViewCharts[viewType];

  const fakeMetricsResult: MetricsResult = useMemo(
    () => ({
      metrics: fakeMetrics(instanceName, 'LIVE', chartProps),
      isLoading: false,
    }),
    [chartProps, instanceName],
  );

  return (
    <GridContainer {...containerProps}>
      {chartProps.map((chart) => (
        <LineCard
          key={chartKey(chart)}
          multiChartProps={chart}
          context={chartContext}
          metricsResult={fakeMetricsResult}
          expandProps={{ isExpanded: false }}
          timeRangePadded={TIME_RANGE_PADDED}
          addMinMaxBands={true}
          pills={getPills(chart, false, false, fakeMetricsResult.metrics)}
          isCmiAvailable={false}
          optionalFeatures={OptionalFeatures.Aura}
          timeRange={{ startTime: new Date(), endTime: new Date() }}
        />
      ))}
    </GridContainer>
  );
};

export const UpgradeMetricsView = ({ instance, viewType, chartContext }: MetricsViewProps) => {
  const activeProject = useActiveProject();
  const upgradeModal = useModal(MODAL_TYPE.UPGRADE);

  return (
    <>
      <UpgradeOverlay
        title="Upgrade to Professional for metrics"
        body="Upgrading to professional enables access to a broad range of different instance and database metrics."
        onUpgrade={() => upgradeModal.open({ project: activeProject, instance: instance!, targetTier: undefined })}
      >
        <FakeMetricsView instanceName={instance?.name ?? ''} viewType={viewType} chartContext={chartContext} />
      </UpgradeOverlay>
    </>
  );
};

export const NoInstancesView = ({ viewType, chartContext }: { viewType: ChartType; chartContext: ChartContext }) => {
  return (
    <NoInstancesOverlay
      title="No instances found"
      body="Create an instance to enable access to a broad range of different instance and database metrics."
    >
      <div className="m-4 flex flex-col gap-4">
        <FakeMetricsView instanceName="fake" viewType={viewType} chartContext={chartContext} />
      </div>
    </NoInstancesOverlay>
  );
};

export default MetricsView;
